import { message, notification } from "antd";
import { searchCompanies } from "./service";
import { Effect, Reducer, AnyAction } from '@umijs/max';

export interface OrganizationSearchRequest {
    page?: number;
    organization_ids?: number[];
    description?: string;
    locations?: number[];
    industries?: number[];
    industries_exclude?: number[];
    keywords?: number[];
    employees_start?: number;
    employees_end?: number;
    founded_date_start?: string;
    founded_date_end?: string;
    revenue_start?: number;
    revenue_end?: number;
    domains?: string[];
    funding_types?: string[];
    funding_total_start?: number;
    funding_total_end?: number;
    funding_event_date_featured_start?: string;
    funding_event_date_featured_end?: string;
    job_post_title?: string;
    job_post_description?: string;
    job_post_is_remote?: boolean;
    job_post_is_active?: boolean;
    job_post_date_featured_start?: string;
    job_post_date_featured_end?: string;
    job_post_locations?: number[];
}

export interface SimplifiedOrganization {
    id: number;
    name: string;
    legal_name: string;
    website: string | null;
    domain: string | null;
    domain_rank: number | null;
    logo: string | null;
    description: string | null;
    founded_date: string | null;
    operating_status: string | null;
    employees_range: string | null;
    employees_amount: number | null;
    location: string | null;
    industries: Array<{
        id: number;
        name: string;
    }>;
    keywords: Array<{
        id: number;
        name: string;
    }>;
    linkedin_public_identifier: string | null;
    linkedin_url: string | null;
    date_updated: string;
}

export interface SimplifiedOrganizationSearchResponse {
    search_results: SimplifiedOrganization[];
    total_search_results: number;
}

interface CompanyModelState {
    searchValue: string;
    filters: Partial<OrganizationSearchRequest>;
    companyData: {
        pages: {
            [key: number]: {
                data: SimplifiedOrganization[];
                total: number;
            }
        };
        total: number;
    };
    currentPage: number;
    pageSize: number;
    selectedRows: number[];
    filterDrawerVisible: boolean;
}

interface CompanyModelType {
    namespace: 'company';
    state: CompanyModelState;
    effects: {
        fetchCompanies: Effect;
        changePage: Effect;
    };
    reducers: {
        updateState: Reducer<CompanyModelState>;
        updateSearchValue: Reducer<CompanyModelState>;
        setCompaniesForPage: Reducer<CompanyModelState>;
        resetFilters: Reducer<CompanyModelState>;
        toggleFilterDrawer: Reducer<CompanyModelState>;
        updateFilter: Reducer<CompanyModelState>;
        addToSelectedRows: Reducer<CompanyModelState>;
        removeFromSelectedRows: Reducer<CompanyModelState>;
        clearSelectedRows: Reducer<CompanyModelState>;
        setSelectedRows: Reducer<CompanyModelState>;
        resetAll: Reducer<CompanyModelState>;
    };
}

const Model: CompanyModelType = {
    namespace: 'company',
    state: {
        searchValue: '',
        filters: {},
        companyData: {
            pages: {},
            total: 0
        },
        currentPage: 1,
        pageSize: 10,
        selectedRows: [],
        filterDrawerVisible: false,
    },
    effects: {
        *fetchCompanies({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.company);

                const current = 1;

                const searchParams: OrganizationSearchRequest = {
                    ...state.filters,
                    ...(payload as Partial<OrganizationSearchRequest>),
                    page: current,
                };

                // Check if we have any valid search parameters
                const hasValidParams = Object.entries(searchParams).some(([key, value]) => {
                    if (key === 'page' || key === '_timestamp') return false;
                    if (Array.isArray(value) && value.length > 0) return true;
                    if (value !== undefined && value !== null && value !== '') return true;
                    return false;
                });

                if (!hasValidParams) {
                    message.error('Please provide search terms or apply filters before searching.');
                    return;
                }

                // Reset companyData before fetching new results
                yield put({
                    type: 'updateState',
                    payload: {
                        companyData: {
                            pages: {},
                            total: 0
                        }
                    }
                });

                yield put({
                    type: 'clearSelectedRows'
                });

                const response: SimplifiedOrganizationSearchResponse = yield call(searchCompanies, searchParams);

                console.log('response', response);

                yield put({
                    type: 'setCompaniesForPage',
                    payload: {
                        page: current,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        currentPage: current
                    }
                });
            } catch (error) {
                if (error && (error as any).response && (error as any).response.status === 400) {
                    notification.error({
                        message: 'Invalid Search',
                        description: (error as any).response.data.message,
                    });
                } else {
                    message.error('Failed to fetch company data');
                }
                console.log('Error fetching companies:', error);
            }
        },
        *changePage({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.company);
                const targetPage = payload.page;

                if (state.companyData.pages[targetPage]?.data?.length > 0) {
                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: targetPage
                        }
                    });
                    return;
                }

                const searchParams: OrganizationSearchRequest = {
                    ...state.filters,
                    page: targetPage,
                };

                const response: SimplifiedOrganizationSearchResponse = yield call(searchCompanies, searchParams);

                yield put({
                    type: 'setCompaniesForPage',
                    payload: {
                        page: targetPage,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        currentPage: targetPage
                    }
                });
            } catch (error) {
                message.error('Failed to fetch company data for page');
                console.error('Error fetching companies for page:', error);
            }
        }
    },
    reducers: {
        updateState(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            if (Object.keys(payload).every(key => state[key as keyof CompanyModelState] === payload[key])) {
                return state;
            }
            return {
                ...state,
                ...payload
            };
        },
        updateSearchValue(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            if (state.searchValue === payload) {
                return state;
            }

            return {
                ...state,
                searchValue: payload,
            };
        },
        setCompaniesForPage(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            const { page, data, total } = payload;

            return {
                ...state,
                companyData: {
                    ...state.companyData,
                    pages: {
                        ...state.companyData.pages,
                        [page]: {
                            data,
                            total
                        }
                    },
                    total
                }
            };
        },
        resetFilters(state: CompanyModelState): CompanyModelState {
            return {
                ...state,
                filters: {},
                searchValue: '',
                companyData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1
            };
        },
        toggleFilterDrawer(state: CompanyModelState): CompanyModelState {
            return {
                ...state,
                filterDrawerVisible: !state.filterDrawerVisible
            };
        },
        updateFilter(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            const { field, value } = payload;

            if (state.filters[field as keyof Partial<OrganizationSearchRequest>] === value) {
                return state;
            }

            return {
                ...state,
                filters: {
                    ...state.filters,
                    [field]: value
                }
            };
        },
        addToSelectedRows(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            if (state.selectedRows.includes(payload)) {
                return state;
            }

            return {
                ...state,
                selectedRows: [...state.selectedRows, payload]
            };
        },
        removeFromSelectedRows(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            if (!state.selectedRows.includes(payload)) {
                return state;
            }

            return {
                ...state,
                selectedRows: state.selectedRows.filter(id => id !== payload)
            };
        },
        clearSelectedRows(state: CompanyModelState): CompanyModelState {
            if (state.selectedRows.length === 0) {
                return state;
            }

            return {
                ...state,
                selectedRows: []
            };
        },
        setSelectedRows(state: CompanyModelState, { payload }: AnyAction): CompanyModelState {
            const newSelectedRows = Array.isArray(payload) ? payload.map(Number) : [];
            
            if (JSON.stringify(state.selectedRows) === JSON.stringify(newSelectedRows)) {
                return state;
            }

            return {
                ...state,
                selectedRows: newSelectedRows
            };
        },
        resetAll(state: CompanyModelState): CompanyModelState {
            return {
                ...state,
                searchValue: '',
                filters: {},
                companyData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1,
                pageSize: 10,
                selectedRows: [],
                filterDrawerVisible: false,
            }
        }
    }
}

export default Model;
